import { useEffect, useMemo, useState } from 'react'
import { type TextInputSelectionChangeEvent } from 'react-native'
import { Input, type InputProps, type InputVariant } from '~/components/ui/input'
import { cn } from '~/lib/utils'

const formatThousandsDigits = (digits: string) => {
  if (!digits) {
    return ''
  }

  const characters: string[] = []
  let digitIndex = digits.length - 1
  let groupCount = 0

  while (digitIndex >= 0) {
    characters.push(digits[digitIndex])

    groupCount += 1

    if (groupCount === 3 && digitIndex > 0) {
      characters.push(' ')
      groupCount = 0
    }

    digitIndex -= 1
  }

  return characters.reverse().join('')
}

const extractDigits = (text: string) => text.replace(/\D/g, '')

const trimLeadingZeros = (digits: string) => digits.replace(/^0+(?=\d)/, '')

const getDigitsFromValue = (value: number | undefined, suffixZeros: number) => {
  if (value === undefined || value === null || !Number.isFinite(value)) {
    return ''
  }

  if (value === 0) {
    return '0'
  }

  const scaledValue = Math.floor(value / 10 ** suffixZeros)

  return scaledValue > 0 ? String(scaledValue) : ''
}

const countDigitsBeforePosition = (formattedValue: string, position: number) => {
  const clampedPosition = Math.max(0, Math.min(position, formattedValue.length))
  let digitCount = 0

  for (let characterIndex = 0; characterIndex < clampedPosition; characterIndex += 1) {
    const character = formattedValue[characterIndex]

    if (character >= '0' && character <= '9') {
      digitCount += 1
    }
  }

  return digitCount
}

const getPositionForDigitIndex = (formattedValue: string, targetDigitIndex: number) => {
  if (targetDigitIndex <= 0) {
    return 0
  }

  let digitCount = 0

  for (let characterIndex = 0; characterIndex < formattedValue.length; characterIndex += 1) {
    const character = formattedValue[characterIndex]

    if (character >= '0' && character <= '9') {
      digitCount += 1

      if (digitCount === targetDigitIndex) {
        return characterIndex + 1
      }
    }
  }

  return formattedValue.length
}

export function PriceInput({
  value,
  onChange,
  placeholder,
  className,
  inputClassName,
  variant = 'box',
  unit = '€',
  ...inputProps
}: {
  value?: number
  onChange: (next?: number) => void
  placeholder?: string
  className?: string
  inputClassName?: string
  variant?: InputVariant
  unit?: string
} & Omit<
  InputProps,
  | 'value'
  | 'onChangeText'
  | 'onChange'
  | 'type'
  | 'formatted'
  | 'variant'
  | 'placeholder'
  | 'className'
  | 'inputClassName'
  | 'unit'
>) {
  const suffixZeros = 3
  const suffixZerosText = '0'.repeat(suffixZeros)

  const [digitsDown, setDigitsDown] = useState<string>(() => getDigitsFromValue(value, suffixZeros))
  const [selection, setSelection] = useState<{ start: number; end: number } | undefined>(undefined)

  useEffect(() => {
    const nextDigits = getDigitsFromValue(value, suffixZeros)

    if (nextDigits !== digitsDown) {
      setDigitsDown(nextDigits)
    }
  }, [digitsDown, suffixZeros, value])

  const formattedDisplay = useMemo(() => {
    return formatThousandsDigits(digitsDown)
  }, [digitsDown])

  const handleSelectionChange = (selectionEvent: TextInputSelectionChangeEvent) => {
    setSelection(selectionEvent.nativeEvent.selection)
  }

  const handleChangeText = (text: string) => {
    const previousFormattedDisplay = formattedDisplay
    const previousSelection = selection ?? {
      start: previousFormattedDisplay.length,
      end: previousFormattedDisplay.length,
    }

    const previousSelectionDigitStart = countDigitsBeforePosition(
      previousFormattedDisplay,
      previousSelection.start
    )
    const previousSelectionDigitEnd = countDigitsBeforePosition(
      previousFormattedDisplay,
      previousSelection.end
    )

    const removedDigitCount = previousSelectionDigitEnd - previousSelectionDigitStart

    const rawDigits = extractDigits(text)
    const rawInsertedDigitCount = rawDigits.length - (digitsDown.length - removedDigitCount)

    const shouldDropTrailingZeros =
      rawInsertedDigitCount > 1 &&
      rawDigits.length > suffixZeros &&
      rawDigits.endsWith(suffixZerosText)

    const normalizedDigits = shouldDropTrailingZeros ? rawDigits.slice(0, -suffixZeros) : rawDigits

    const trimmedDigits = trimLeadingZeros(normalizedDigits)

    if (trimmedDigits !== digitsDown) {
      const insertedDigitCount = trimmedDigits.length - (digitsDown.length - removedDigitCount)
      const nextCaretDigitIndex = Math.max(0, previousSelectionDigitStart + insertedDigitCount)

      const nextFormattedDisplay = formatThousandsDigits(trimmedDigits)
      const nextCaretPosition = getPositionForDigitIndex(nextFormattedDisplay, nextCaretDigitIndex)

      setDigitsDown(trimmedDigits)
      setSelection({ start: nextCaretPosition, end: nextCaretPosition })

      onChange(trimmedDigits ? Number(trimmedDigits) * 10 ** suffixZeros : undefined)

      return
    }

    const nextCaretPosition = getPositionForDigitIndex(
      formattedDisplay,
      previousSelectionDigitStart
    )

    if (selection?.start !== nextCaretPosition || selection?.end !== nextCaretPosition) {
      setSelection({ start: nextCaretPosition, end: nextCaretPosition })
    }
  }

  const suffix = unit ? `${suffixZerosText} ${unit}` : suffixZerosText

  return (
    <Input
      type="numeric"
      formatted
      variant={variant}
      placeholder={placeholder}
      value={formattedDisplay}
      onChangeText={handleChangeText}
      selection={selection}
      onSelectionChange={handleSelectionChange}
      className={cn(className)}
      inputClassName={cn('text-right font-medium', inputClassName)}
      allowNegative={false}
      suffix={suffix}
      labelsClassName="whitespace-nowrap font-normal"
      {...inputProps}
    />
  )
}
